import { LDadd, LDdelete, LDinsertResource, LDdeleteResource, LDupdate, LDreplace, LDupdateList, LDnoop } from './ld-patch-model';

function BridgeTransformProcessor(utils) {

	// The transformations in this file are exactly the same as the API and persistence layer transformations.
	// Apart from applyClientAddServerAddTransform which isn't async

	function TransformationException(message) {
		this.message = message;
		this.name = "TransformationException";
	}

	this.applyTransform = function (clientObject, serverObject) {

		if (clientObject.constructor === LDdelete) {
			return applyClientDeleteTransform(clientObject, serverObject);
		} else if (clientObject.constructor === LDadd) {
			return applyClientAddTransform(clientObject, serverObject);
		} else if (clientObject.constructor === LDupdate) {
			return applyClientUpdateTransform(clientObject, serverObject);
		} else if (clientObject.constructor === LDreplace) {
			return applyClientReplaceTransform(clientObject, serverObject);
		} else if (clientObject.constructor === LDdeleteResource) {
			return applyClientDeleteResourceTransform(clientObject, serverObject);
		} else if (clientObject.constructor === LDinsertResource) {
			return applyClientInsertResourceTransform(clientObject, serverObject);
		} else if (clientObject.constructor === LDupdateList) {
			return applyClientUpdateListTransform(clientObject, serverObject);
		} else {
			throw new TransformationException("Transforming unrecognised object: " + serverObject.constructor.name);
		}
	};

	// Common from here onwards

	function applyClientInsertResourceTransform(clientObject, serverObject) {

		const clientIRI = clientObject.getIRI().getText();

		if (serverObject.constructor === LDdeleteResource || serverObject.constructor === LDinsertResource) {
			const serverIRI = serverObject.getIRI().getText();
			// Inserting or deleting different resources so no conflict
			if (clientIRI !== serverIRI) return clientObject;
			// It's the same resource so ignore client action - give server priority
			return new LDnoop();
		}
		else {
			const serverSubjectURI = serverObject.getSubject().getText();
			if (clientIRI !== serverSubjectURI) return clientObject;
			// The fact that somebody is trying to change or delete it implies it already exists so we ignore the insert
			return new LDnoop();
		}
	}

	function applyClientDeleteResourceTransform(clientObject, serverObject) {

		const clientIRI = clientObject.getIRI().getText();

		if (serverObject.constructor === LDdeleteResource || serverObject.constructor === LDinsertResource) {
			const serverIRI = serverObject.getIRI().getText();
			// Inserting or deleting different resources so no conflict
			if (clientIRI !== serverIRI) return clientObject;
			// It's the same resource so ignore client action - give server priority
			return new LDnoop();
		}
		// For everything else the client resource deletion wins
		return clientObject;
	}


	function applyClientAddTransform(clientObject, serverObject) {

		if (serverObject.constructor === LDdelete) {
			return applyClientAddServerDeleteTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDadd) {
			return applyClientAddServerAddTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdate) {
			return applyClientAddServerUpdateTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDreplace) {
			return applyClientAddServerReplaceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDdeleteResource) {
			return applyClientAddServerDeleteResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDinsertResource) {
			return applyClientAddServerInsertResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdateList) {
			return applyClientAddServerUpdateListTransform(clientObject);
		} else {
			throw new TransformationException("Transforming unrecognised object: " + serverObject.constructor.name);
		}
	}

	function applyClientAddServerAddTransform(clientObject, serverObject) {
		// This will cover scenarios 3 and 4

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientObjectGraph = clientObject.getGraph().getText();
		const clientPredicateGraph = clientObject.getPredicateGraph() ? clientObject.getPredicateGraph().getText() : null;
		
		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverPredicateGraph = serverObject.getPredicateGraph() ? serverObject.getPredicateGraph().getText() : null;

		if (clientSubjectURI !== serverSubjectURI) {
			// SCENARIO 4"
			return clientObject;
		}

		const uniqueClient = utils.isUniqueLang(clientSubjectURI, clientObjectGraph, clientPredicateURI);
		const uniqueServer = utils.isUniqueLang(serverSubjectURI, clientObjectGraph, serverPredicateURI);
		if (clientPredicateURI !== serverPredicateURI) {
			// Is it possible that one of the predicates is entailable via a property chain
			const isClientEntailablePredicate = utils.isEntailedPredicatePair(clientPredicateURI, serverPredicateURI);
			const isServerEntailablePredicate = utils.isEntailedPredicatePair(serverPredicateURI, clientPredicateURI);
			// If it is then the entailed value might also cause a constraint violation on uniqueness so we need to do the check
			if (isClientEntailablePredicate) {
				// Server entails a value for client potentially
				// Compute state for server resource
				const serverState = utils.computeResourceState(isClientEntailablePredicate, serverObject.getObject().getText(), clientObjectGraph);
				// Assuming language container for now
				// Does client value match a value in the server state
				const doesValueExist = serverState && serverState.find(item => {
					// If language values only permit one per language any language match is a conflict
					if (uniqueClient && item.lang === clientObject.getObject().getLanguage()) return true;
					return item.resource === clientObject.getObject().getText() && item.lang === clientObject.getObject().getLanguage();
				});
				if (doesValueExist) {
					// Server entails the value for the client so do nothing on the server
					return new LDnoop();
				}
			}
			else if (isServerEntailablePredicate) {
				// Client entails a value for server potentially
				// Compute resource state for client resource
				const clientState = utils.computeResourceState(isServerEntailablePredicate, clientObject.getObject().getText(), clientObjectGraph);
				// Assuming language container for now
				// Does client state match server value
				const doesValueExist = clientState && clientState.find(item => {
					// If language values only permit one per language any language match is a conflict
					if (uniqueServer && item.lang === serverObject.getObject().getLanguage()) return true;
					return item.resource === serverObject.getObject().getText() && item.lang === serverObject.getObject().getLanguage();
				});
				if (doesValueExist) {
					// Client entails the value for the server
					let ldr = new LDreplace();
					ldr.setSubject(serverObject.getSubject());
					ldr.setPredicate(serverObject.getPredicate());
					ldr.setObject(serverObject.getObject());
					ldr.setPredicateReplacement(clientObject.getPredicate());
					ldr.setObjectReplacement(clientObject.getObject());
					ldr.setGraph(clientObject.getGraph());
					// SCENARIO 4b
					return ldr;
				}
			}
			// SCENARIO 4
			return clientObject;
		}

		if (clientObjectObject !== serverObjectObject) {
			// If there is a unique language constraint on the predicate it will potentially be scenario 4a
			if (uniqueClient) {
				const clientObjectLang = clientObject.getObject().getLanguage();
				const serverObjectLang = serverObject.getObject().getLanguage();
				if (serverObjectLang && clientObjectLang === serverObjectLang) {
					// SCENARIO 4a
					return new LDnoop();
				}
			}

			// Is this a functional property? (Predicates are the same at this point)
			const isFunctional = utils.isFunctional(clientSubjectURI, clientObjectGraph, clientPredicateURI, clientPredicateGraph, serverPredicateGraph);
			if (isFunctional) {
				// Scenario 4aa
				return new LDnoop();
			}

			// Otherwise it is still scenario 4
			// SCENARIO 4
			return clientObject;
		}

		// SCENARIO 3
		return new LDnoop();
	}

	function applyClientAddServerUpdateTransform(clientObject, serverObject) {
		// This will cover scenarios 32, 33 and 34

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI) {
			// SCENARIO 33
			return clientObject;
		}
		if (clientObjectObject === serverObjectReplacementObject) {
			// SCENARIO 34
			return new LDnoop();
		}

		// SCENARIO 32
		return new LDnoop();
	}

	function applyClientAddServerReplaceTransform(clientObject, serverObject) {
		// Scenarios 49, 49a and 49b

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverPredicateReplacementURI = serverObject.getPredicateReplacement().getText();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateReplacementURI
			&& clientObjectObject === serverObjectReplacementObject) {
			// SCENARIO 49b
			return new LDnoop();
		}

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 49a
			return clientObject;
		}

		// SCENARIO 49 and 49b
		return new LDnoop();

	}

	function applyClientAddServerDeleteTransform(clientObject, serverObject) {
		// This will cover scenarios 7 and 8

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 8
			return clientObject;
		}

		// SCENARIO 7
		return new LDnoop();
	}

	function applyClientAddServerDeleteResourceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		// Server has already deleted so ignore any operations against same resource
		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientAddServerInsertResourceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		// Server has already inserted so ignore any operations against same resource
		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientAddServerUpdateListTransform(clientObject) {
		// Scenarios 10 and 14
		return clientObject;
	}

	function applyClientDeleteTransform(clientObject, serverObject) {

		if (serverObject.constructor === LDdelete) {
			return applyClientDeleteServerDeleteTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDadd) {
			return applyClientDeleteServerAddTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdate) {
			return applyClientDeleteServerUpdateTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDreplace) {
			return applyClientDeleteServerReplaceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDdeleteResource) {
			return applyClientDeleteServerDeleteResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDinsertResource) {
			return applyClientDeleteServerInsertResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdateList) {
			return applyClientDeleteServerUpdateListTransform(clientObject);
		} else {
			throw new TransformationException("Transforming unrecognised object: " + serverObject.constructor.name);
		}
	}

	function applyClientDeleteServerDeleteTransform(clientObject, serverObject) {
		// This will cover scenarios 1 and 2

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 2
			return clientObject;
		}
		// SCENARIO 1
		return new LDnoop();
	}

	function applyClientDeleteServerAddTransform(clientObject, serverObject) {
		// This will cover scenarios 5 and 6

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();

		// Not the same subject so this is scenario 5
		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 5"
			return clientObject;
		}

		// SCENARIO 6
		return new LDnoop();
	}

	function applyClientDeleteServerUpdateTransform(clientObject, serverObject) {
		// This will cover scenarios 24, 25 and 26

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI) {
			// SCENARIO 25
			return clientObject;
		}
		if (clientObjectObject === serverObjectReplacementObject) {
			// SCENARIO 26
			return new LDnoop();
		}

		// SCENARIO 24
		return new LDnoop();
	}

	function applyClientDeleteServerReplaceTransform(clientObject, serverObject) {
		// Scenarios 51, 51a and 51b

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverPredicateReplacementURI = serverObject.getPredicateReplacement().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateReplacementURI
			&& clientObjectObject === serverObjectReplacementObject) {
			// SCENARIO 51b
			return clientObject;
		}

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 51a
			return clientObject;
		}

		// SCENARIO 51
		return new LDnoop();
	}

	function applyClientDeleteServerDeleteResourceTransform(clientObject, serverObject) {
		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		// Server has already deleted so ignore any operations against same resource
		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientDeleteServerInsertResourceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientDeleteServerUpdateListTransform(clientObject) {
		// This will cover scenarios 9 and 13
		return clientObject;
	}

	// ===================== REPLACE PROCESSING ======================

	function applyClientReplaceTransform(clientObject, serverObject) {

		if (serverObject.constructor === LDdelete) {
			return applyClientReplaceServerDeleteTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDadd) {
			return applyClientReplaceServerAddTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdate) {
			return applyClientReplaceServerUpdateTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDreplace) {
			return applyClientReplaceServerReplaceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDdeleteResource) {
			return applyClientReplaceServerDeleteResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDinsertResource) {
			return applyClientReplaceServerInsertResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdateList) {
			return applyClientReplaceServerUpdateListTransform(clientObject);
		} else {
			throw new TransformationException("Transforming unrecognised object: " + serverObject.constructor.name);
		}
	}

	function applyClientReplaceServerDeleteTransform(clientObject, serverObject) {
		// Scenarios 50, 50a and 50b

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientPredicateReplacementURI = clientObject.getPredicateReplacement().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();

		if (clientSubjectURI === serverSubjectURI
			&& clientPredicateReplacementURI === serverPredicateURI && clientObjectReplacementObject === serverObjectObject) {
			// SCENARIO 50b
			let ldd = new LDdelete();
			ldd.setSubject(clientObject.getSubject());
			ldd.setPredicate(clientObject.getPredicate());
			ldd.setObject(clientObject.getObject());
			ldd.setGraph(clientObject.getGraph());
			return ldd;
		}

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 50a
			return clientObject;
		}

		// SCENARIO 50
		let lda = new LDadd();
		lda.setSubject(clientObject.getSubject());
		lda.setPredicate(clientObject.getPredicateReplacement());
		lda.setObject(clientObject.getObjectReplacement());
		lda.setGraph(clientObject.getGraph());
		return lda;
	}

	function applyClientReplaceServerAddTransform(clientObject, serverObject) {
		// This will cover scenarios 48, 48a and 48b

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientPredicateReplacementURI = clientObject.getPredicateReplacement().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();

		// The triple being added is the same as the replacement triple
		// We delete the replaced triple
		if (clientSubjectURI === serverSubjectURI && clientPredicateReplacementURI === serverPredicateURI
			&& clientObjectReplacementObject === serverObjectObject) {
			// SCENARIO 48b
			let ldd = new LDdelete();
			ldd.setSubject(clientObject.getSubject());
			ldd.setPredicate(clientObject.getPredicate());
			ldd.setObject(clientObject.getObject());
			ldd.setGraph(clientObject.getGraph());
			return ldd;
		}

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI || clientObjectObject !== serverObjectObject) {
			// SCENARIO 48a
			return clientObject;
		}

		// SCENARIO 48
		return clientObject;
	}

	function applyClientReplaceServerReplaceTransform(clientObject, serverObject) {
		// This will cover scenarios 54, 54a, 54b, 54c and 54d

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientPredicateReplacementURI = clientObject.getPredicateReplacement().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverPredicateReplacementURI = serverObject.getPredicateReplacement().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI !== serverSubjectURI) {
			// So different subjects
			// SCENARIO 54d
			return clientObject;
		}
		if (clientPredicateURI === serverPredicateURI && clientObjectObject === serverObjectObject
			&& clientPredicateReplacementURI === serverPredicateReplacementURI && clientObjectReplacementObject === serverObjectReplacementObject) {
			// So identical operations on both sides
			// SCENARIO 54
			return new LDnoop();
		}
		if (clientPredicateURI === serverPredicateURI && clientObjectObject === serverObjectObject) {
			// Same triple replaced with different things
			// SCENARIO 54a
			return new LDnoop();
		}
		if (clientPredicateReplacementURI === serverPredicateReplacementURI && clientObjectReplacementObject === serverObjectReplacementObject) {
			// So different triples replaced with the same thing - delete client triple being replaced
			// SCENARIO 54b
			let ldd = new LDdelete();
			ldd.setSubject(clientObject.getSubject());
			ldd.setPredicate(clientObject.getPredicate());
			ldd.setObject(clientObject.getObject());
			ldd.setGraph(clientObject.getGraph());
			return ldd;
		}

		// SCENARIO 54c
		// Different triples replaced with different things
		return clientObject;
	}

	function applyClientReplaceServerUpdateTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientPredicateReplacementURI = clientObject.getPredicateReplacement().getText();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateURI && clientObjectObject === serverObjectObject) {
			// SCENARIO 53
			let ldr = new LDreplace();
			ldr.setSubject(clientObject.getSubject());
			ldr.setPredicate(clientObject.getPredicate());
			ldr.setObject(serverObject.getObjectReplacement());
			ldr.setPredicateReplacement(clientObject.getPredicateReplacement());
			ldr.setObjectReplacement(clientObject.getObjectReplacement());
			ldr.setGraph(clientObject.getGraph());
			return ldr;
		}

		if (clientSubjectURI === serverSubjectURI && clientPredicateReplacementURI === serverPredicateURI
			&& clientObjectReplacementObject === serverObjectReplacementObject) {
			// SCENARIO 53a
			let ldd = new LDdelete();
			ldd.setSubject(clientObject.getSubject());
			ldd.setPredicate(clientObject.getPredicate());
			ldd.setObject(clientObject.getObject());
			ldd.setGraph(clientObject.getGraph());
			return ldd;
		}

		if (clientSubjectURI === serverSubjectURI && clientPredicateReplacementURI === serverPredicateURI
			&& clientObjectReplacementObject === serverObjectObject) {
			// SCENARIO 53b
			let ldd = new LDdelete();
			ldd.setSubject(clientObject.getSubject());
			ldd.setPredicate(clientObject.getPredicate());
			ldd.setObject(clientObject.getObject());
			ldd.setGraph(clientObject.getGraph());
			return ldd;
		}

		// SCENARIO 53c and 53d
		return clientObject;
	}

	function applyClientReplaceServerDeleteResourceTransform(clientObject, serverObject) {
		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		// Server has already deleted so ignore any operations against same resource
		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientReplaceServerInsertResourceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientReplaceServerUpdateListTransform(clientObject) {
		// This is scenario 40 and scenario 42
		return clientObject;
	}

	// ===================== UPDATE PROCESSING ======================

	function applyClientUpdateTransform(clientObject, serverObject) {

		if (serverObject.constructor === LDdelete) {
			return applyClientUpdateServerDeleteTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDadd) {
			return applyClientUpdateServerAddTransform(clientObject);
		} else if (serverObject.constructor === LDupdate) {
			return applyClientUpdateServerUpdateTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDreplace) {
			return applyClientUpdateServerReplaceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDdeleteResource) {
			return applyClientUpdateServerDeleteResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDinsertResource) {
			return applyClientUpdateServerInsertResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdateList) {
			return applyClientUpdateServerUpdateListTransform(clientObject);
		} else {
			throw new TransformationException("Transforming unrecognised object: " + serverObject.constructor.name);
		}
	}

	function applyClientUpdateServerDeleteTransform(clientObject, serverObject) {
		// This will cover scenarios 28, 39 and 30

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI) {
			// SCENARIO 29
			return clientObject;
		}
		if (clientObjectReplacementObject === serverObjectObject) {
			// SCENARIO 30
			return clientObject;
		}

		// Otherwise it is scenario 28
		let lda = new LDadd();
		lda.setSubject(clientObject.getSubject());
		lda.setPredicate(clientObject.getPredicate());
		lda.setObject(clientObject.getObjectReplacement());
		lda.setGraph(clientObject.getGraph());
		return lda;
	}

	function applyClientUpdateServerAddTransform(clientObject) {
		// This will cover scenarios 36, 37, and 38
		return clientObject;
	}

	function applyClientUpdateServerUpdateTransform(clientObject, serverObject) {
		// This will cover scenarios 45, 46 and 47

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();
		const clientObjectGraph = clientObject.getGraph().getText();
		const clientPredicateGraph = clientObject.getPredicateGraph() ? clientObject.getPredicateGraph().getText() : null;

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();
		const serverPredicateGraph = serverObject.getPredicateGraph() ? serverObject.getPredicateGraph().getText() : null;

		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI) {
			// SCENARIO 47
			return clientObject;
		}

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateURI) {
			const isFunctional = utils.isFunctional(clientSubjectURI, clientObjectGraph, clientPredicateURI, clientPredicateGraph, serverPredicateGraph);
			// SCENARIO 46a
			if (isFunctional) {
				return new LDnoop();
			}
		}
	
		if (clientObjectObject !== serverObjectObject) {
			// Different values being updated
			if (clientObjectReplacementObject === serverObjectReplacementObject) {
				// Different values being updated to the same value
				// SCENARIO 47a
				let ldd = new LDdelete();
				ldd.setSubject(clientObject.getSubject());
				ldd.setPredicate(clientObject.getPredicate());
				ldd.setObject(clientObject.getObject());
				ldd.setGraph(clientObject.getGraph());
				return ldd;
			}
			// SCENARIO 47b
			return clientObject;
		}
		if (clientObjectReplacementObject !== serverObjectReplacementObject) {
			// SCENARIO 46
			return new LDnoop();
		}

		// SCENARIO 45
		return new LDnoop();
	}

	function applyClientUpdateServerReplaceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();
		const clientObjectObject = clientObject.getObject().getValue();
		const clientObjectReplacementObject = clientObject.getObjectReplacement().getValue();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();
		const serverObjectObject = serverObject.getObject().getValue();
		const serverPredicateReplacementURI = serverObject.getPredicateReplacement().getText();
		const serverObjectReplacementObject = serverObject.getObjectReplacement().getValue();

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateURI && clientObjectObject === serverObjectObject) {
			// SCENARIO 52
			return new LDnoop();
		}

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateReplacementURI
			&& clientObjectReplacementObject === serverObjectReplacementObject) {
			// SCENARIO 52a
			let ldd = new LDdelete();
			ldd.setSubject(clientObject.getSubject());
			ldd.setPredicate(clientObject.getPredicate());
			ldd.setObject(clientObject.getObject());
			ldd.setGraph(clientObject.getGraph());
			return ldd;
		}

		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateReplacementURI
			&& clientObjectObject === serverObjectReplacementObject) {
			// SCENARIO 52b
			return clientObject;
		}

		// SCENARIO 52c and 52d
		return clientObject;
	}

	function applyClientUpdateServerDeleteResourceTransform(clientObject, serverObject) {
		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		// Server has already deleted so ignore any operations against same resource
		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientUpdateServerInsertResourceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientUpdateServerUpdateListTransform(clientObject) {
		// This is scenario 40 and scenario 42
		return clientObject;
	}

	// ===================== LIST PROCESSING =======================

	function applyClientUpdateListTransform(clientObject, serverObject) {

		if (serverObject.constructor === LDdelete) {
			return applyClientUpdateListServerDeleteTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDadd) {
			return clientObject;
		} else if (serverObject.constructor === LDupdate) {
			return clientObject;
		} else if (serverObject.constructor === LDreplace) {
			return clientObject;
		} else if (serverObject.constructor === LDdeleteResource) {
			return applyClientUpdateListServerDeleteResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDinsertResource) {
			return applyClientUpdateListServerInsertResourceTransform(clientObject, serverObject);
		} else if (serverObject.constructor === LDupdateList) {
			return applyClientUpdateListServerUpdateListTransform(clientObject, serverObject);
		} else {
			throw new TransformationException("Transforming unrecognised object: " + serverObject.constructor.name);
		}
	}

	function applyClientUpdateListServerDeleteTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const clientPredicateURI = clientObject.getPredicate().getText();

		const serverSubjectURI = serverObject.getSubject().getText();
		const serverPredicateURI = serverObject.getPredicate().getText();

		// The server has deleted the list so nothing to do
		if (clientSubjectURI === serverSubjectURI && clientPredicateURI === serverPredicateURI) return new LDnoop();

		return clientObject;
	}

	function applyClientUpdateListServerDeleteResourceTransform(clientObject, serverObject) {
		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		// Server has already deleted so ignore any operations against same resource
		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientUpdateListServerInsertResourceTransform(clientObject, serverObject) {

		const clientSubjectURI = clientObject.getSubject().getText();
		const serverIRI = serverObject.getIRI().getText();

		if (serverIRI === clientSubjectURI) return new LDnoop();

		return clientObject;
	}

	function applyClientUpdateListServerUpdateListTransform(clientObject, serverObject) {
		// This will cover scenarios 17, 18, 19, 20, 21, 22, 22a, 23 and 23s

		const co = clientObject;
		const so = serverObject;

		// For now we assume start and end indexes (allowing for beginning and
		// end of list) are the same because we only allow an insertion
		const clientSubjectURI = co.getSubject().getText();
		const clientPredicateURI = co.getPredicate().getText();
		const clientStartIndex = parseInt(co.getStartIndex());
		const clientEndIndex = parseInt(co.getEndIndex());
		const clientReplacementList = co.getReplacementList();

		const serverSubjectURI = so.getSubject().getText();
		const serverPredicateURI = so.getPredicate().getText();
		const serverStartIndex = parseInt(so.getStartIndex());
		const serverReplacementList = so.getReplacementList();

		// Not the same subject or predicate so this is scenario 17, 20, 22a and 23a
		if (clientSubjectURI !== serverSubjectURI || clientPredicateURI !== serverPredicateURI) return clientObject;

		if (clientStartIndex === serverStartIndex) {
			if (clientReplacementList.length === 0) {
				if (serverReplacementList.length === 0) {
					// SCENARIO 21, where M = N
					return new LDnoop();
				}
			}
			else if (serverReplacementList.length > 0) {
				// We use array index zero because we only expect one item in update for now
				if (clientReplacementList[0].getText() === serverReplacementList[0].getText()) {
					// SCENARIO 18
					return new LDnoop();
				}
			}
		}

		// c' is the transform to apply to s on the server
		let cprime = new LDupdateList();
		cprime.setSubject(co.getSubject());
		cprime.setPredicate(co.getPredicate());
		cprime.setReplacementList(clientReplacementList);
		cprime.setGraph(co.getGraph());

		// If no client replacements then this is a client delete scenario
		if (clientReplacementList.length === 0) {
			if (serverReplacementList.length === 0) {
				// Delete on client and server ...
				// Scenario 21
				if (clientStartIndex < serverStartIndex) {
					// Scenario 21, where M < N
					cprime.setStartIndex(clientStartIndex);
					cprime.setEndIndex(clientEndIndex);
				} else {
					// Scenario 21, where M > N
					cprime.setStartIndex(clientStartIndex - 1);
					cprime.setEndIndex(clientEndIndex - 1);
				}
			} else {
				// Delete on client and insert on server
				// Scenario 23
				if (clientStartIndex < serverStartIndex) {
					// Scenario 23, where M <= N
					cprime.setStartIndex(clientStartIndex);
					cprime.setEndIndex(clientEndIndex);
				} else {
					// Scenario 23, where M > N
					cprime.setStartIndex(clientStartIndex + 1);
					cprime.setEndIndex(clientEndIndex + 1);
				}
			}
		} else {
			if (serverReplacementList.length === 0) {
				// Insert on client and delete on server
				// Scenario 22
				if (clientStartIndex <= serverStartIndex
					&& clientStartIndex !== -1) {
					// Scenario 22, where M <= N
					cprime.setStartIndex(clientStartIndex);
					cprime.setEndIndex(clientEndIndex);
				} else {
					// Scenario 22, where M > N
					if (clientStartIndex !== -1) {
						// SCENARIO 22, where M >= N
						cprime.setStartIndex(clientStartIndex - 1);
						cprime.setEndIndex(clientEndIndex - 1);
					} else {
						// Insert at the end of the list
						cprime.setStartIndex(clientStartIndex);
						cprime.setEndIndex(clientEndIndex);
					}
				}
			} else {
				// Insert on client and server ...
				if (clientStartIndex !== -1
					&& ((clientStartIndex < serverStartIndex) || serverStartIndex === -1)) {
					// Scenario 19, where M < N
					cprime.setStartIndex(clientStartIndex);
					cprime.setEndIndex(clientEndIndex);
				} else {
					// Scenario 19, where M > N
					if (clientStartIndex !== -1) {
						cprime.setStartIndex(clientStartIndex + 1);
						cprime.setEndIndex(clientEndIndex + 1);
					} else {
						// Insert at the end of the list
						cprime.setStartIndex(clientStartIndex);
						cprime.setEndIndex(clientEndIndex);
					}
				}
			}
		}

		return cprime;
	}

}

export default BridgeTransformProcessor;